import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { theme } from '../../../styles/theme';
import { AdminContainer } from '../AdminShell';
import { ALL_SYSTEMS } from '../../lutron/content';
import {
  analyzeImage, suggestKind, processImage, PRESETS,
  type AnalyzedImage, type ImageKind,
} from '../../../lib/imageProcessing';

type LutronAsset = {
  id: string;
  line_slug: string;
  kind: string;
  url: string;
  storage_path: string | null;
  caption: string | null;
  alt_text: string | null;
  position: number;
  source: string;
  source_url: string | null;
  bytes: number | null;
  target_name: string | null;
  created_at: string;
};

const LINES = [
  { slug: 'caseta',     name: 'Caséta',     accent: '#5B9BD5', tier: 'entry' },
  { slug: 'radiora3',   name: 'RadioRA 3',  accent: '#B08D5B', tier: 'mid' },
  { slug: 'maestro',    name: 'Maestro',    accent: '#4A4A4A', tier: 'cableado' },
  { slug: 'wallbox',    name: 'Wallbox',    accent: '#E07B3F', tier: 'value' },
  { slug: 'vive',       name: 'Vive',       accent: '#3D8B7A', tier: 'commercial' },
  { slug: 'homeworks',  name: 'HomeWorks',  accent: '#C9A961', tier: 'luxury' },
];

const KINDS = [
  { value: 'hero',         label: '🌅 Hero (foto principal del header)', desc: 'Foto grande de fondo del hero. 1 por línea. Idealmente 1920×900.' },
  { value: 'keypad',       label: '🎛 Keypad / Producto', desc: 'Foto del keypad, dimmer o pieza icónica. Múltiples permitidas.' },
  { value: 'lifestyle',    label: '🏡 Lifestyle / Ambiente', desc: 'Foto en contexto: sala, oficina, hotel. Para sección de aplicaciones.' },
  { value: 'product',      label: '📦 Producto plain', desc: 'Foto del producto sobre fondo blanco/neutro.' },
  { value: 'video',        label: '🎬 Video (mp4/webm)', desc: 'Demo o reel embebido en la landing.' },
  { value: 'spec_diagram', label: '📐 Diagrama técnico', desc: 'Diagrama de instalación, dimensiones, conexión.' },
  { value: 'ambient',      label: '🌃 Ambient', desc: 'Foto de ambiente generada o de stock para hero secundario.' },
];

export function AdminMarketingLutron() {
  const [activeLine, setActiveLine] = useState<string>('caseta');
  const [assets, setAssets] = useState<LutronAsset[]>([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);

  useEffect(() => { reload(); }, []);

  async function reload() {
    setLoading(true);
    const { data } = await supabase
      .from('lutron_assets')
      .select('*')
      .order('line_slug')
      .order('kind')
      .order('position');
    setAssets((data as LutronAsset[]) ?? []);
    setLoading(false);
  }

  const byLine = useMemo(() => {
    const map: Record<string, LutronAsset[]> = {};
    for (const a of assets) {
      if (!map[a.line_slug]) map[a.line_slug] = [];
      map[a.line_slug].push(a);
    }
    return map;
  }, [assets]);

  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    for (const l of LINES) c[l.slug] = byLine[l.slug]?.length ?? 0;
    return c;
  }, [byLine]);

  const lineAssets = byLine[activeLine] ?? [];
  const groupedByKind = useMemo(() => {
    const map: Record<string, LutronAsset[]> = {};
    for (const a of lineAssets) {
      if (!map[a.kind]) map[a.kind] = [];
      map[a.kind].push(a);
    }
    return map;
  }, [lineAssets]);

  const activeLineMeta = LINES.find(l => l.slug === activeLine)!;

  async function deleteAsset(asset: LutronAsset) {
    if (!confirm(`¿Eliminar este asset? "${asset.caption ?? asset.kind}"`)) return;
    if (asset.storage_path) {
      await supabase.storage.from('lutron-marketing').remove([asset.storage_path]);
    }
    await supabase.from('lutron_assets').delete().eq('id', asset.id);
    reload();
  }

  return (
    <AdminContainer
      title="Fotos & videos Lutron"
      eyebrow="Marketing assets por línea"
      action={
        <button onClick={() => setShowUpload(true)} style={btnPrimary}>
          + Subir asset
        </button>
      }
    >
      {/* Selector de línea */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
        gap: 8,
        marginBottom: 24,
      }}>
        {LINES.map(line => (
          <button
            key={line.slug}
            onClick={() => setActiveLine(line.slug)}
            style={{
              background: activeLine === line.slug ? theme.bgFooter : theme.bg,
              border: `1.5px solid ${activeLine === line.slug ? line.accent : theme.border}`,
              borderTop: `4px solid ${line.accent}`,
              padding: '12px 14px',
              cursor: 'pointer',
              textAlign: 'left',
              fontFamily: theme.font.sans,
            }}
          >
            <div style={{ fontWeight: 700, color: theme.brand, fontSize: 14 }}>{line.name}</div>
            <div style={{ fontSize: 10, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 2 }}>
              {line.tier} · {counts[line.slug] ?? 0} fotos
            </div>
          </button>
        ))}
      </div>

      <div style={{
        marginBottom: 16,
        padding: 14,
        background: theme.bgFooter,
        borderLeft: `4px solid ${activeLineMeta.accent}`,
      }}>
        <div style={{ fontSize: 11, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700 }}>
          Estás editando
        </div>
        <div style={{ fontFamily: theme.font.head, fontSize: 22, color: theme.brand, marginTop: 4 }}>
          Lutron {activeLineMeta.name}
        </div>
        <div style={{ fontSize: 12, color: theme.textMuted, marginTop: 4 }}>
          {lineAssets.length} assets cargados · estos aparecen en <code style={{ fontFamily: theme.font.mono, fontSize: 11 }}>nuled.mx/lutron/{activeLine}</code>
        </div>
      </div>

      {loading && <div style={{ color: theme.textMuted }}>Cargando…</div>}

      {!loading && lineAssets.length === 0 && (
        <div style={{
          padding: 60, textAlign: 'center',
          background: theme.bg, border: `1px dashed ${theme.border}`,
          color: theme.textMuted, fontSize: 14,
        }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>📸</div>
          <div style={{ fontFamily: theme.font.head, fontSize: 18, color: theme.brand, marginBottom: 6 }}>
            Sin fotos para {activeLineMeta.name}
          </div>
          <div>Pega URLs de lutron.com o sube archivos directamente.</div>
          <button onClick={() => setShowUpload(true)} style={{ ...btnPrimary, marginTop: 16 }}>
            + Agregar primer asset
          </button>
        </div>
      )}

      {/* Assets agrupados por kind */}
      {KINDS.map(k => {
        const items = groupedByKind[k.value] ?? [];
        if (items.length === 0) return null;
        return (
          <div key={k.value} style={{ marginBottom: 24 }}>
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
              marginBottom: 12, gap: 12,
            }}>
              <div>
                <div style={{ fontFamily: theme.font.head, fontSize: 16, color: theme.brand, fontWeight: 600 }}>
                  {k.label}
                </div>
                <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 2 }}>
                  {k.desc} · {items.length} cargado{items.length !== 1 ? 's' : ''}
                </div>
              </div>
            </div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
              gap: 12,
            }}>
              {items.map(a => (
                <AssetCard key={a.id} asset={a} onDelete={() => deleteAsset(a)} />
              ))}
            </div>
          </div>
        );
      })}

      {showUpload && (
        <UploadModal
          line={activeLineMeta}
          keypadNames={ALL_SYSTEMS.find(s => s.slug === activeLine)?.keypads?.map(k => k.name) ?? []}
          onClose={() => setShowUpload(false)}
          onUploaded={() => { setShowUpload(false); reload(); }}
        />
      )}
    </AdminContainer>
  );
}

function AssetCard({ asset, onDelete }: { asset: LutronAsset; onDelete: () => void }) {
  const isVideo = asset.kind === 'video' || /\.(mp4|webm)$/i.test(asset.url);
  return (
    <div style={{
      background: theme.bg, border: `1px solid ${theme.border}`,
      display: 'flex', flexDirection: 'column',
    }}>
      <div style={{
        aspectRatio: '4/3',
        background: theme.bgImage,
        backgroundImage: !isVideo ? `url(${asset.url})` : undefined,
        backgroundSize: 'cover', backgroundPosition: 'center',
        position: 'relative',
      }}>
        {isVideo && (
          <video
            src={asset.url}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            muted
          />
        )}
        <div style={{
          position: 'absolute', top: 6, right: 6,
          fontSize: 9, padding: '2px 6px',
          background: 'rgba(0,0,0,0.6)', color: '#fff', fontWeight: 700,
          textTransform: 'uppercase', letterSpacing: 0.4,
        }}>
          {asset.source}
        </div>
      </div>
      <div style={{ padding: 10 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: theme.brand, lineHeight: 1.3 }}>
          {asset.caption ?? '(sin caption)'}
        </div>
        {asset.bytes && (
          <div style={{ fontSize: 9, color: theme.textMuted, marginTop: 2, fontFamily: theme.font.mono }}>
            {(asset.bytes / 1024).toFixed(0)} KB · pos {asset.position}
          </div>
        )}
        <div style={{ display: 'flex', gap: 4, marginTop: 8 }}>
          <a href={asset.url} target="_blank" rel="noopener" style={mini}>Abrir ↗</a>
          <button onClick={onDelete} style={{ ...mini, color: theme.err, border: `1px solid ${theme.err}` }}>
            Eliminar
          </button>
        </div>
      </div>
    </div>
  );
}

function UploadModal({
  line, keypadNames, onClose, onUploaded,
}: {
  line: typeof LINES[number];
  keypadNames: string[];
  onClose: () => void;
  onUploaded: () => void;
}) {
  const [mode, setMode] = useState<'url' | 'file'>('url');
  const [url, setUrl] = useState('');
  const [kind, setKind] = useState<string>('keypad');
  const [targetName, setTargetName] = useState<string>(keypadNames[0] ?? '');
  const [caption, setCaption] = useState('');
  const [position, setPosition] = useState(0);
  const [file, setFile] = useState<File | null>(null);
  const [analysis, setAnalysis] = useState<AnalyzedImage | null>(null);
  const [suggestion, setSuggestion] = useState<{ kind: ImageKind; confidence: string; reason: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [success, setSuccess] = useState<number>(0);
  const [autoProcess, setAutoProcess] = useState(true);

  const needsTarget = kind === 'keypad' || kind === 'product';
  const effectiveTarget = needsTarget && targetName ? targetName : null;

  // Cuando cambia file o url, analizar dimensiones y sugerir kind
  async function analyzeAndSuggest(source: File | string) {
    try {
      const a = await analyzeImage(source);
      setAnalysis(a);
      const s = suggestKind(a);
      setSuggestion(s);
      // Si el usuario no ha cambiado kind manualmente, adoptar la sugerencia
      setKind(s.kind);
    } catch (e: any) {
      setErr(`No se pudo analizar la imagen: ${e.message}`);
    }
  }

  async function handleFileChange(f: File | null) {
    setFile(f);
    setErr(null);
    setAnalysis(null);
    setSuggestion(null);
    if (f) await analyzeAndSuggest(f);
  }

  async function handleUrlBlur() {
    if (!url.trim()) return;
    setErr(null);
    setAnalysis(null);
    setSuggestion(null);
    try { await analyzeAndSuggest(url.trim()); }
    catch { /* la edge function lo intentará igual */ }
  }

  async function uploadFromUrl() {
    setBusy(true); setErr(null);

    // Si autoProcess está activo Y pudimos analizar (CORS permite), procesamos local
    // y subimos como file. Si no, dejamos que la edge function la baje tal cual.
    if (autoProcess && analysis) {
      try {
        const tmpFile = new File([analysis.blob], 'remote-source.jpg', { type: 'image/jpeg' });
        const processed = await processImage(tmpFile, kind as ImageKind);
        await uploadBlobToBucket(processed);
        setBusy(false);
        setSuccess(s => s + 1);
        setUrl(''); setCaption(''); setAnalysis(null); setSuggestion(null);
        return;
      } catch (e: any) {
        // Si falla el procesamiento local, fallback a edge function (descarga raw)
        console.warn('Auto-process URL falló, usando edge function:', e);
      }
    }

    // Fallback: edge function descarga al bucket sin procesamiento
    const session = await supabase.auth.getSession();
    const token = session.data.session?.access_token;
    if (!token) { setErr('No hay sesión activa'); setBusy(false); return; }

    const res = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/fetch-asset-from-url`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          url: url.trim(),
          line_slug: line.slug,
          kind, caption, position,
          target_name: effectiveTarget,
        }),
      }
    );
    const data = await res.json();
    setBusy(false);
    if (!data.ok) { setErr(data.error || 'Error desconocido'); return; }
    setSuccess(s => s + 1);
    setUrl(''); setCaption(''); setAnalysis(null); setSuggestion(null);
  }

  async function uploadFromFile() {
    if (!file) { setErr('Selecciona un archivo'); return; }
    setBusy(true); setErr(null);

    try {
      const toUpload: Blob = autoProcess
        ? await processImage(file, kind as ImageKind)
        : file;
      await uploadBlobToBucket(toUpload);
      setBusy(false);
      setSuccess(s => s + 1);
      setFile(null); setCaption(''); setAnalysis(null); setSuggestion(null);
    } catch (e: any) {
      setErr(e.message);
      setBusy(false);
    }
  }

  async function uploadBlobToBucket(blob: Blob) {
    const id = crypto.randomUUID();
    const ext = blob.type.includes('webp') ? 'webp'
              : blob.type.includes('png') ? 'png'
              : blob.type.includes('mp4') ? 'mp4'
              : blob.type.includes('webm') ? 'webm'
              : 'jpg';
    const path = `${line.slug}/${kind}/${id}.${ext}`;

    const { error: upErr } = await supabase.storage
      .from('lutron-marketing')
      .upload(path, blob, { contentType: blob.type, upsert: false });
    if (upErr) throw new Error(`Upload: ${upErr.message}`);

    const { data: pub } = supabase.storage.from('lutron-marketing').getPublicUrl(path);

    const { error: insErr } = await supabase.from('lutron_assets').insert({
      line_slug: line.slug, kind, url: pub.publicUrl, storage_path: path,
      caption: caption || null, position, source: 'manual',
      bytes: blob.size, target_name: effectiveTarget,
      width: analysis?.width ?? null, height: analysis?.height ?? null,
    });
    if (insErr) throw new Error(`DB: ${insErr.message}`);
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: 20,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: theme.bg, border: `1px solid ${theme.border}`,
        width: '100%', maxWidth: 560, padding: 28, maxHeight: '90vh', overflowY: 'auto',
        position: 'relative',
      }}>
        <button type="button" onClick={onClose} aria-label="Cerrar" title="Cerrar" style={{ position: 'absolute', top: 10, right: 14, background: 'transparent', border: 'none', color: theme.textMuted, fontSize: 26, lineHeight: 1, cursor: 'pointer', padding: 4, zIndex: 5 }}>×</button>
        <div style={{ borderTop: `4px solid ${line.accent}`, marginBottom: 16, paddingTop: 8 }}>
          <div style={{ fontSize: 11, color: theme.accentDark, textTransform: 'uppercase', letterSpacing: 1.5, fontWeight: 700 }}>
            Subir asset · {line.name}
          </div>
          <h2 style={{ fontFamily: theme.font.head, fontSize: 20, color: theme.brand, margin: '4px 0 0', fontWeight: 400 }}>
            Foto, video o ilustración
          </h2>
        </div>

        {/* Mode toggle */}
        <div style={{ display: 'flex', gap: 0, marginBottom: 16, border: `1px solid ${theme.border}` }}>
          <ModeBtn active={mode === 'url'} onClick={() => setMode('url')}>🔗 URL externa</ModeBtn>
          <ModeBtn active={mode === 'file'} onClick={() => setMode('file')}>📁 Archivo local</ModeBtn>
        </div>

        {mode === 'url' && (
          <Field label="URL de la imagen / video">
            <input
              value={url}
              onChange={e => setUrl(e.target.value)}
              onBlur={handleUrlBlur}
              placeholder="https://www.lutron.com/.../keypad-palladiom.jpg"
              style={inputStyle}
            />
            <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 4 }}>
              Pega URL y haz click fuera — analizamos dimensiones y te sugerimos el mejor uso.
            </div>
          </Field>
        )}

        {mode === 'file' && (
          <Field label="Archivo (JPG/PNG/WebP/MP4 ≤ 20 MB)">
            <input
              type="file"
              accept="image/*,video/*"
              onChange={e => handleFileChange(e.target.files?.[0] ?? null)}
              style={{ ...inputStyle, padding: 8 }}
            />
            {file && <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 4 }}>
              {file.name} · {(file.size / 1024).toFixed(0)} KB
            </div>}
          </Field>
        )}

        {/* Análisis + sugerencia automática */}
        {analysis && (
          <div style={{
            marginTop: 14, padding: 12,
            background: theme.bgFooter,
            border: `1px solid ${theme.border}`,
            borderLeft: `4px solid ${suggestion?.confidence === 'high' ? theme.accentDark : suggestion?.confidence === 'low' ? theme.err : theme.warn}`,
          }}>
            <div style={{ fontSize: 11, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 700, marginBottom: 6 }}>
              🔍 Análisis automático
            </div>
            <div style={{ fontSize: 12, color: theme.brand, fontFamily: theme.font.mono }}>
              {analysis.width}×{analysis.height}px · ratio {analysis.aspectRatio.toFixed(2)} ·{' '}
              {analysis.isPanoramic ? 'panorámica' : analysis.isPortrait ? 'vertical ⚠' : analysis.isSquare ? 'cuadrada' : 'horizontal'}
            </div>
            {suggestion && (
              <div style={{ marginTop: 8 }}>
                <div style={{ fontSize: 13, color: theme.brand, fontWeight: 600 }}>
                  💡 Sugerimos: <strong>{KINDS.find(k => k.value === suggestion.kind)?.label}</strong>
                  <span style={{
                    marginLeft: 8, fontSize: 10, padding: '1px 6px',
                    background: suggestion.confidence === 'high' ? theme.accentDark : suggestion.confidence === 'low' ? theme.err : theme.warn,
                    color: '#fff', textTransform: 'uppercase', letterSpacing: 0.4,
                  }}>
                    {suggestion.confidence === 'high' ? 'alta confianza' : suggestion.confidence === 'low' ? 'baja · revisa' : 'media'}
                  </span>
                </div>
                <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 4, lineHeight: 1.5 }}>
                  {suggestion.reason}
                </div>
              </div>
            )}
            <label style={{
              display: 'flex', alignItems: 'center', gap: 8, marginTop: 10,
              fontSize: 12, color: theme.brand, cursor: 'pointer',
            }}>
              <input
                type="checkbox"
                checked={autoProcess}
                onChange={e => setAutoProcess(e.target.checked)}
                style={{ accentColor: theme.accent }}
              />
              <span>
                Procesar automáticamente al subir <strong>({PRESETS[kind as ImageKind]?.description})</strong>
              </span>
            </label>
          </div>
        )}

        <div style={{ marginTop: 14 }}>
          <Field label="Tipo de asset">
            <select value={kind} onChange={e => setKind(e.target.value)} style={inputStyle}>
              {KINDS.map(k => <option key={k.value} value={k.value}>{k.label}</option>)}
            </select>
          </Field>
        </div>

        {needsTarget && keypadNames.length > 0 && (
          <div style={{ marginTop: 14 }}>
            <Field label="¿A cuál keypad pertenece esta foto?">
              <select value={targetName} onChange={e => setTargetName(e.target.value)} style={inputStyle}>
                <option value="">— Sin asociar (foto general) —</option>
                {keypadNames.map(name => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
              <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 4 }}>
                Esta foto se mostrará exactamente en la card de <strong style={{ color: theme.brand }}>{targetName || '(ninguno específico)'}</strong> dentro de la landing.
              </div>
            </Field>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12, marginTop: 14 }}>
          <Field label="Caption / descripción">
            <input
              value={caption}
              onChange={e => setCaption(e.target.value)}
              placeholder="Ej. Sunnata Touch Keypad en Satin Snow"
              style={inputStyle}
            />
          </Field>
          <Field label="Posición (orden)">
            <input
              type="number"
              value={position}
              onChange={e => setPosition(Number(e.target.value))}
              style={inputStyle}
            />
          </Field>
        </div>

        {err && (
          <div style={{
            color: theme.err, fontSize: 13, marginTop: 12,
            padding: 10, background: 'rgba(220,38,38,0.05)',
            border: `1px solid ${theme.err}`,
          }}>{err}</div>
        )}

        {success > 0 && (
          <div style={{
            color: theme.accentDark, fontSize: 13, marginTop: 12,
            padding: 10, background: 'rgba(87,255,154,0.08)',
            border: `1px solid ${theme.accentDark}`,
          }}>
            ✓ {success} asset{success !== 1 ? 's' : ''} subido{success !== 1 ? 's' : ''} · puedes seguir agregando más
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 20, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={btnSecondary}>Cerrar</button>
          <button
            disabled={busy || (mode === 'url' ? !url : !file)}
            onClick={mode === 'url' ? uploadFromUrl : uploadFromFile}
            style={{
              ...btnPrimary,
              opacity: busy || (mode === 'url' ? !url : !file) ? 0.5 : 1,
              cursor: busy ? 'wait' : 'pointer',
            }}
          >
            {busy ? 'Subiendo…' : 'Subir →'}
          </button>
        </div>

        <div style={{ marginTop: 18, paddingTop: 14, borderTop: `1px solid ${theme.border}`, fontSize: 11, color: theme.textMuted, lineHeight: 1.6 }}>
          <strong style={{ color: theme.brand }}>Tip:</strong> Para fotos oficiales Lutron, copia la URL de la imagen desde lutron.com (clic derecho → "Copiar dirección de imagen") y pégala aquí. La descargamos y queda en tu bucket. Si bloquean hotlinking, usa el modo Archivo.
          <br />
          {success > 0 && onUploaded && (
            <button onClick={onUploaded} style={{
              marginTop: 10, background: 'transparent',
              border: 'none', color: theme.accentDark, cursor: 'pointer',
              fontFamily: theme.font.sans, fontSize: 11, fontWeight: 700, textDecoration: 'underline', padding: 0,
            }}>
              Cerrar y refrescar lista →
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function ModeBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick} style={{
      flex: 1,
      background: active ? theme.brand : theme.bg,
      color: active ? theme.textOnBrand : theme.brand,
      border: 'none',
      padding: '10px 14px',
      fontSize: 12, fontWeight: 700,
      letterSpacing: 0.4, textTransform: 'uppercase',
      cursor: 'pointer', fontFamily: theme.font.sans,
    }}>{children}</button>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'block' }}>
      <div style={{
        fontSize: 11, color: theme.textMuted,
        textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700,
        marginBottom: 6,
      }}>{label}</div>
      {children}
    </label>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  background: theme.bg,
  border: `1px solid ${theme.border}`,
  padding: '10px 12px',
  color: theme.text,
  fontSize: 13,
  fontFamily: theme.font.sans,
  outline: 'none',
  boxSizing: 'border-box',
};

const btnPrimary: React.CSSProperties = {
  background: theme.accent,
  color: theme.brand,
  border: 'none',
  padding: '10px 18px',
  fontSize: 12, fontWeight: 700,
  letterSpacing: 0.4, textTransform: 'uppercase',
  cursor: 'pointer', fontFamily: theme.font.sans,
};

const btnSecondary: React.CSSProperties = {
  background: 'transparent', color: theme.brand,
  border: `1px solid ${theme.border}`,
  padding: '10px 18px',
  fontSize: 12, fontWeight: 700,
  letterSpacing: 0.4, textTransform: 'uppercase',
  cursor: 'pointer', fontFamily: theme.font.sans,
};

const mini: React.CSSProperties = {
  flex: 1,
  fontSize: 10, padding: '4px 6px',
  background: 'transparent', color: theme.brand,
  border: `1px solid ${theme.border}`,
  textDecoration: 'none', textAlign: 'center',
  fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4,
  cursor: 'pointer', fontFamily: theme.font.sans,
};
